import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import App from './App.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { initSentry, SentryErrorBoundary } from './utils/sentry.js';
import { setupReactDevTools, devLog } from './utils/devTools.js';
import swManager from './utils/serviceWorker.js';
import { NotificationProvider } from './components/notifications';
import './index.css';

// Initialize development tools
if (import.meta.env.DEV) {
  devLog.info('Starting application in development mode');
  setupReactDevTools();
}

// Initialize Sentry before anything else
initSentry();

// Register Service Worker for PWA functionality
if ('serviceWorker' in navigator) {
  // Register service worker in both development and production for testing
  devLog.info('Registering Service Worker...');

  swManager.register().then((success) => {
    if (success) {
      devLog.info('✅ Service Worker registered successfully');
    } else {
      devLog.warn('❌ Service Worker registration failed');
    }
  });

  // Set up service worker event listeners
  swManager.on('onUpdateAvailable', () => {
    devLog.info('🔄 Service Worker update available');
    // Notification will be handled by PWANotificationHandler component
  });

  swManager.on('onOffline', () => {
    devLog.info('📱 App is offline');
    // Notification will be handled by PWANotificationHandler component
  });

  swManager.on('onOnline', () => {
    devLog.info('🌐 App is back online');
    // Notification will be handled by PWANotificationHandler component
  });
} else {
  devLog.warn('❌ Service Worker not supported in this browser');
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      onError: (error) => {
        if (import.meta.env.DEV) {
          devLog.error('React Query Error:', error);
        }
      },
    },
    mutations: {
      onError: (error) => {
        if (import.meta.env.DEV) {
          devLog.error('React Query Mutation Error:', error);
        }
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={ErrorFallback} showDialog>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </SentryErrorBoundary>
  </React.StrictMode>
);

// Fallback component for error boundary
function ErrorFallback({ error, resetError }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Something went wrong
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            An unexpected error occurred. Our team has been notified.
          </p>
        </div>
        <div className="space-y-4">
          <div className="rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Details</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error.message}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={resetError}
              className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Try Again
            </button>
            <button
              onClick={() => (window.location.href = '/')}
              className="flex-1 rounded-md bg-gray-600 px-4 py-2 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
